import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import {
  Save, Loader2, Plus, Trash2, Video, ChevronDown, ChevronRight, X, CheckCircle2,
  Eye, Presentation, Edit3, Film, GripVertical, FileText, User, ChevronLeft,
  Sparkles, LayoutDashboard, Clock, BookOpen, Undo2
} from 'lucide-react';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors, DragOverlay } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { formationModules, trainers } from '../data/formations';

const API = process.env.REACT_APP_BACKEND_URL;

// ─── SORTABLE LESSON ITEM ───
const SortableLessonItem = ({ id, lesson, isActive, hasOverride, hasVideo, hasSlides, isCustom, index, onClick, onDelete, onPreview }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.4 : 1, zIndex: isDragging ? 50 : 'auto' };

  return (
    <div ref={setNodeRef} style={style} data-testid={`formateur-lesson-${id}`}
      className={`group relative flex items-center gap-2 rounded-lg px-3 py-2.5 cursor-pointer transition-all duration-150 ${isActive ? 'bg-[#0d2847] ring-1 ring-cyan-500/30' : 'hover:bg-white/[0.03]'}`}
    >
      <button {...attributes} {...listeners} className="p-1 rounded text-gray-700 hover:text-gray-400 cursor-grab active:cursor-grabbing flex-shrink-0" data-testid={`drag-handle-${id}`}>
        <GripVertical size={14} />
      </button>
      <div className="flex-1 min-w-0" onClick={onClick}>
        <div className="flex items-center gap-2">
          <div className={`w-6 h-6 rounded flex items-center justify-center flex-shrink-0 text-[10px] font-bold ${hasVideo ? 'bg-emerald-500/15 text-emerald-400' : 'bg-white/5 text-gray-600'}`}>
            {hasVideo ? <Film size={11} /> : index + 1}
          </div>
          <p className="text-[13px] font-medium text-gray-200 truncate">{lesson.title}</p>
        </div>
        <div className="flex items-center gap-1.5 mt-1 ml-8">
          {lesson.duration && <span className="text-[10px] text-gray-600 flex items-center gap-0.5"><Clock size={9} />{lesson.duration}</span>}
          {hasOverride && <span className="text-[9px] px-1.5 py-px rounded-full bg-cyan-500/10 text-cyan-400 font-medium">Modifié</span>}
          {hasSlides && <span className="text-[9px] px-1.5 py-px rounded-full bg-violet-500/10 text-violet-400 font-medium">Slides</span>}
          {hasVideo && <span className="text-[9px] px-1.5 py-px rounded-full bg-emerald-500/10 text-emerald-400 font-medium">Vidéo</span>}
        </div>
      </div>
      <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
        <button onClick={(e) => { e.stopPropagation(); onPreview(); }} className="p-1.5 rounded-md text-gray-600 hover:text-cyan-400 hover:bg-cyan-500/10 transition-all" title="Preview"><Eye size={13} /></button>
        {isCustom && <button onClick={(e) => { e.stopPropagation(); onDelete(); }} className="p-1.5 rounded-md text-gray-600 hover:text-red-400 hover:bg-red-500/10 transition-all" title="Supprimer"><Trash2 size={13} /></button>}
      </div>
    </div>
  );
};

// ─── STUDENT PREVIEW MODAL ───
const StudentPreview = ({ lesson, moduleName, trainerName, onClose }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  if (!lesson) return null;

  const generateSlides = (l) => {
    if (!l.script) return [{ type: 'title', title: l.title, subtitle: moduleName, points: [] }];
    const slides = [];
    const lines = l.script.split('\n').map(ln => ln.trim()).filter(Boolean);
    const introLines = [];
    let i = 0;
    while (i < lines.length && !lines[i].startsWith('Points') && !lines[i].startsWith('Conclusion') && !lines[i].startsWith('- ')) {
      introLines.push(lines[i]); i++;
    }
    slides.push({ type: 'title', title: l.title, subtitle: moduleName, points: introLines.length > 0 ? [introLines.join(' ')] : [] });
    const bullets = [];
    for (; i < lines.length; i++) {
      if (lines[i].startsWith('Conclusion')) break;
      if (lines[i].startsWith('Points')) continue;
      if (lines[i].startsWith('- ')) bullets.push(lines[i].replace(/^- /, ''));
    }
    for (let c = 0; c < bullets.length; c += 4) {
      slides.push({ type: 'content', title: c === 0 ? 'Points clés' : 'Points clés (suite)', points: bullets.slice(c, c + 4) });
    }
    const conclusionLines = [];
    let found = false;
    for (const line of lines) {
      if (line.startsWith('Conclusion')) { found = true; continue; }
      if (found) conclusionLines.push(line.replace(/^"/, '').replace(/"$/, ''));
    }
    if (conclusionLines.length > 0) slides.push({ type: 'conclusion', title: 'À retenir', points: conclusionLines });
    return slides.length > 0 ? slides : [{ type: 'title', title: l.title, subtitle: moduleName, points: [] }];
  };

  const slides = generateSlides(lesson);
  const slide = slides[currentSlide] || slides[0];

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center" style={{ backgroundColor: 'rgba(0,0,0,0.9)' }} data-testid="student-preview-modal">
      <div className="w-full max-w-5xl mx-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-cyan-500/15 flex items-center justify-center"><Eye size={15} className="text-cyan-400" /></div>
            <div>
              <p className="text-white text-sm font-semibold">Aperçu Étudiant</p>
              <p className="text-gray-500 text-[11px]">Voici ce que tes élèves verront</p>
            </div>
          </div>
          <button onClick={onClose} className="w-9 h-9 rounded-lg flex items-center justify-center text-gray-500 hover:text-white hover:bg-white/10 transition-all" data-testid="close-preview-btn"><X size={18} /></button>
        </div>

        <div className="flex gap-5">
          {/* Slide viewer */}
          <div className="flex-1">
            <div className="relative rounded-2xl overflow-hidden" style={{ backgroundColor: '#080b16', border: '1px solid rgba(255,255,255,0.06)', aspectRatio: '16/9' }}>
              <div className="absolute inset-0 flex flex-col justify-center px-12 py-10">
                {slide.type === 'title' && (
                  <>
                    <p className="text-[11px] font-bold tracking-[3px] uppercase mb-4 text-cyan-400">{slide.subtitle}</p>
                    <h2 className="text-white text-2xl font-bold mb-4" style={{ fontFamily: '"Space Grotesk", sans-serif' }}>{slide.title}</h2>
                    {slide.points.map((p, i) => <p key={i} className="text-gray-400 text-sm leading-relaxed max-w-[85%]">{p}</p>)}
                  </>
                )}
                {slide.type === 'content' && (
                  <>
                    <p className="text-[11px] font-bold tracking-[3px] uppercase mb-6 text-cyan-400">{slide.title}</p>
                    <div className="space-y-3">
                      {slide.points.map((p, i) => (
                        <div key={i} className="flex items-start gap-3">
                          <div className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 bg-cyan-500/10 border border-cyan-500/20">
                            <span className="text-[11px] font-bold text-cyan-400">{i + 1}</span>
                          </div>
                          <p className="text-gray-300 text-sm leading-relaxed">{p}</p>
                        </div>
                      ))}
                    </div>
                  </>
                )}
                {slide.type === 'conclusion' && (
                  <>
                    <p className="text-[11px] font-bold tracking-[3px] uppercase mb-6 text-violet-400">{slide.title}</p>
                    {slide.points.map((p, i) => <p key={i} className="text-white text-lg font-semibold italic leading-relaxed">"{p}"</p>)}
                  </>
                )}
              </div>
              <div className="absolute top-4 right-5 text-[11px] text-gray-600 font-mono">{currentSlide + 1} / {slides.length}</div>
              {/* PiP */}
              <div className="absolute bottom-16 right-5 rounded-xl overflow-hidden flex items-center justify-center" style={{ width: 110, height: 78, backgroundColor: '#0f1220', border: '2px solid rgba(255,255,255,0.1)' }}>
                <div className="text-center"><div className="w-7 h-7 rounded-full mx-auto mb-1 flex items-center justify-center bg-cyan-500/10"><User size={12} className="text-cyan-400" /></div><p className="text-[8px] text-gray-500">{trainerName}</p></div>
              </div>
              {/* Nav */}
              <div className="absolute bottom-4 left-0 right-0 flex items-center justify-between px-5">
                <button onClick={() => setCurrentSlide(Math.max(0, currentSlide - 1))} disabled={currentSlide === 0} className="w-8 h-8 rounded-full flex items-center justify-center disabled:opacity-20 bg-white/5 hover:bg-white/10 transition-all"><ChevronLeft size={14} className="text-white" /></button>
                <div className="flex items-center gap-1.5">{slides.map((_, idx) => <button key={idx} onClick={() => setCurrentSlide(idx)} className="transition-all" style={{ width: idx === currentSlide ? 20 : 6, height: 6, borderRadius: 3, backgroundColor: idx === currentSlide ? '#00c8ff' : 'rgba(255,255,255,0.12)' }} />)}</div>
                <button onClick={() => setCurrentSlide(Math.min(slides.length - 1, currentSlide + 1))} disabled={currentSlide === slides.length - 1} className="w-8 h-8 rounded-full flex items-center justify-center disabled:opacity-20 bg-white/5 hover:bg-white/10 transition-all"><ChevronRight size={14} className="text-white" /></button>
              </div>
            </div>
          </div>
          {/* Notes preview */}
          <div className="w-[240px] rounded-2xl overflow-hidden" style={{ backgroundColor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)' }}>
            <div className="px-4 py-3 border-b border-white/5"><div className="flex items-center gap-2"><FileText size={12} className="text-cyan-400" /><p className="text-[11px] font-bold tracking-wider text-cyan-400">MES NOTES</p></div></div>
            <div className="p-4"><p className="text-gray-600 text-[12px] italic">Zone de prise de notes de l'étudiant...</p></div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── ADD LESSON INLINE FORM ───
const AddLessonForm = ({ onAdd, onCancel, saving }) => {
  const [title, setTitle] = useState('');
  return (
    <div className="rounded-lg p-3 mt-1 bg-cyan-500/[0.03] border border-dashed border-cyan-500/15" data-testid="add-lesson-form">
      <input value={title} onChange={e => setTitle(e.target.value)} placeholder="Titre de la nouvelle leçon..." onKeyDown={e => { if (e.key === 'Enter' && title.trim()) onAdd(title); if (e.key === 'Escape') onCancel(); }}
        className="w-full bg-transparent border-b border-white/10 pb-2 text-white text-[13px] placeholder:text-gray-600 focus:outline-none focus:border-cyan-500/30 mb-2" autoFocus data-testid="new-lesson-title-input" />
      <div className="flex gap-2">
        <button onClick={() => title.trim() && onAdd(title)} disabled={saving || !title.trim()} className="px-4 py-1.5 rounded-lg text-[11px] text-white font-semibold disabled:opacity-40 bg-gradient-to-r from-cyan-500 to-blue-600" data-testid="confirm-add-lesson-btn">{saving ? 'Ajout...' : 'Ajouter'}</button>
        <button onClick={onCancel} className="px-3 py-1.5 text-[11px] text-gray-500 hover:text-gray-300">Annuler</button>
      </div>
    </div>
  );
};

// ─── MAIN PORTAL ───
export default function FormateurPortal() {
  const { token } = useParams();
  const [formateur, setFormateur] = useState(null);
  const [overrides, setOverrides] = useState({});
  const [files, setFiles] = useState({});
  const [sectionOverrides, setSectionOverrides] = useState({});
  const [lessonOrders, setLessonOrders] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeLesson, setActiveLesson] = useState(null);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [uploading, setUploading] = useState(null);
  const [expandedSections, setExpandedSections] = useState({});
  const [showAddLesson, setShowAddLesson] = useState(null);
  const [customLessons, setCustomLessons] = useState([]);
  const [editingSection, setEditingSection] = useState(null);
  const [sectionTitle, setSectionTitle] = useState('');
  const [showAddSection, setShowAddSection] = useState(null);
  const [newSectionTitle, setNewSectionTitle] = useState('');
  const [customSections, setCustomSections] = useState([]);
  const [previewLesson, setPreviewLesson] = useState(null);
  const [dragActiveId, setDragActiveId] = useState(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  const fetchData = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/formateur/${token}`);
      if (!res.ok) throw new Error('Lien invalide');
      const data = await res.json();
      setFormateur(data.formateur);
      setOverrides(data.overrides || {});
      setFiles(data.files || {});
      setSectionOverrides(data.section_overrides || {});
      setLessonOrders(data.lesson_orders || {});
      setCustomLessons(Object.values(data.overrides || {}).filter(o => o.is_custom));
      setCustomSections(Object.values(data.section_overrides || {}).filter(o => o.is_custom));
      // Expand all sections by default on first load
      if (Object.keys(expandedSections).length === 0) {
        const expanded = {};
        const mods = formationModules.filter(m => data.formateur.module_ids.includes(m.id));
        mods.forEach(mod => { mod.sections.forEach((_, si) => { expanded[`${mod.id}-${si}`] = true; }); });
        setExpandedSections(expanded);
      }
    } catch (e) { setError(e.message); }
    setLoading(false);
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const getModules = () => formateur ? formationModules.filter(m => formateur.module_ids.includes(m.id)) : [];

  const getLessonData = (lesson) => {
    const o = overrides[lesson.id];
    return o ? { ...lesson, title: o.title || lesson.title, description: o.description || lesson.description, script: o.script || lesson.script, duration: o.duration || lesson.duration } : lesson;
  };

  const getSectionTitle = (modId, si, orig) => sectionOverrides[`${modId}_${si}`]?.title || orig;
  const isSectionDeleted = (modId, si) => sectionOverrides[`${modId}_${si}`]?.is_deleted === true;

  // Get ordered lessons for a section
  const getOrderedLessons = (modId, si, baseLessons, sectionCustomLessons) => {
    const sKey = `${modId}_${si}`;
    const order = lessonOrders[sKey];
    const allLessons = [
      ...baseLessons.map(l => ({ ...getLessonData(l), _isCustom: false })),
      ...sectionCustomLessons.map(cl => ({ ...cl, id: cl.lesson_id, _isCustom: true }))
    ];
    if (!order) return allLessons;
    const ordered = [];
    order.forEach(lid => { const found = allLessons.find(l => (l.id || l.lesson_id) === lid); if (found) ordered.push(found); });
    allLessons.forEach(l => { const lid = l.id || l.lesson_id; if (!order.includes(lid)) ordered.push(l); });
    return ordered;
  };

  const openEditor = (lesson) => {
    const d = getLessonData(lesson);
    setEditData({ title: d.title || '', description: d.description || '', script: d.script || '', duration: d.duration || '10:00' });
    setActiveLesson(lesson.id || lesson.lesson_id);
    setSaved(false);
  };

  const handleSave = async () => {
    if (!activeLesson) return;
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/formateur/${token}/lesson/${activeLesson}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(editData),
      });
      if (res.ok) { setSaved(true); await fetchData(); setTimeout(() => setSaved(false), 3000); }
    } catch (e) { console.error(e); }
    setSaving(false);
  };

  const handleUpload = async (lessonId, fileType) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = fileType === 'video' ? 'video/*' : '.pdf,.pptx,.ppt';
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      setUploading(`${lessonId}_${fileType}`);
      const fd = new FormData();
      fd.append('file', file);
      fd.append('file_type', fileType);
      try {
        const res = await fetch(`${API}/api/formateur/${token}/lesson/${lessonId}/upload`, { method: 'POST', body: fd });
        if (res.ok) await fetchData();
        else { const err = await res.json(); alert(err.detail || 'Erreur'); }
      } catch { alert('Erreur réseau'); }
      setUploading(null);
    };
    input.click();
  };

  const handleAddLesson = async (si, title) => {
    if (!title.trim()) return;
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/formateur/${token}/lesson`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ title, description: '', script: '', duration: '10:00', section_index: si }),
      });
      if (res.ok) { setShowAddLesson(null); await fetchData(); }
    } catch (e) { console.error(e); }
    setSaving(false);
  };

  const handleDeleteLesson = async (lessonId) => {
    if (!window.confirm('Supprimer cette leçon ?')) return;
    try {
      await fetch(`${API}/api/formateur/${token}/lesson/${lessonId}`, { method: 'DELETE' });
      if (activeLesson === lessonId) setActiveLesson(null);
      await fetchData();
    } catch (e) { console.error(e); }
  };

  const handleRenameSection = async (modId, si) => {
    if (!sectionTitle.trim()) return;
    try {
      await fetch(`${API}/api/formateur/${token}/section/${modId}/${si}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ title: sectionTitle }),
      });
      setEditingSection(null); await fetchData();
    } catch (e) { console.error(e); }
  };

  const handleDeleteSection = async (modId, si) => {
    if (!window.confirm('Supprimer cette section ?')) return;
    try { await fetch(`${API}/api/formateur/${token}/section/${modId}/${si}`, { method: 'DELETE' }); await fetchData(); } catch (e) { console.error(e); }
  };

  const handleRestoreSection = async (modId, si) => {
    try { await fetch(`${API}/api/formateur/${token}/section/${modId}/${si}/restore`, { method: 'POST' }); await fetchData(); } catch (e) { console.error(e); }
  };

  const handleAddSection = async (modId) => {
    if (!newSectionTitle.trim()) return;
    try {
      await fetch(`${API}/api/formateur/${token}/section`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ title: newSectionTitle, module_id: modId }),
      });
      setShowAddSection(null); setNewSectionTitle(''); await fetchData();
    } catch (e) { console.error(e); }
  };

  // Drag-and-drop handler
  const handleDragEnd = async (event, modId, si, orderedLessons) => {
    const { active, over } = event;
    setDragActiveId(null);
    if (!over || active.id === over.id) return;
    const ids = orderedLessons.map(l => l.id || l.lesson_id);
    const oldIndex = ids.indexOf(active.id);
    const newIndex = ids.indexOf(over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    const newIds = arrayMove(ids, oldIndex, newIndex);
    // Optimistic update
    setLessonOrders(prev => ({ ...prev, [`${modId}_${si}`]: newIds }));
    // Persist
    try {
      await fetch(`${API}/api/formateur/${token}/reorder`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ section_key: `${modId}_${si}`, lesson_ids: newIds }),
      });
    } catch (e) { console.error(e); }
  };

  // Stats
  const getStats = () => {
    const modules = getModules();
    let total = 0, withVideo = 0, withSlides = 0, modified = 0;
    modules.forEach(mod => {
      mod.sections.forEach(s => { s.lessons.forEach(l => { total++; if (files[`${l.id}_video`]) withVideo++; if (files[`${l.id}_slides`]) withSlides++; if (overrides[l.id]) modified++; }); });
    });
    total += customLessons.length;
    return { total, withVideo, withSlides, modified };
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#030014' }}>
      <div className="text-center">
        <Loader2 size={28} className="animate-spin text-cyan-400 mx-auto mb-3" />
        <p className="text-gray-500 text-sm">Chargement du portail...</p>
      </div>
    </div>
  );

  if (error) return (
    <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#030014' }}>
      <div className="text-center"><X size={40} className="text-red-400 mx-auto mb-4" /><h1 className="text-white text-lg font-bold mb-2">Lien invalide</h1><p className="text-gray-500 text-sm">Ce lien d'accès formateur n'existe pas.</p></div>
    </div>
  );

  const modules = getModules();
  const stats = getStats();
  const progress = stats.total > 0 ? Math.round((stats.withVideo / stats.total) * 100) : 0;
  const currentMod = modules[0];
  const trainerName = currentMod?.id ? (trainers[currentMod.id]?.name || formateur?.name) : formateur?.name;

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#030014' }} data-testid="formateur-portal">
      {/* Student Preview Modal */}
      {previewLesson && (
        <StudentPreview lesson={previewLesson} moduleName={currentMod?.title} trainerName={trainerName} onClose={() => setPreviewLesson(null)} />
      )}

      {/* ─── HEADER ─── */}
      <header className="sticky top-0 z-40 backdrop-blur-xl" style={{ backgroundColor: 'rgba(3,0,20,0.85)', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
        <div className="max-w-[1400px] mx-auto px-6 h-[60px] flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center bg-gradient-to-br from-cyan-500 to-blue-600">
              <LayoutDashboard size={16} className="text-white" />
            </div>
            <div>
              <h1 className="text-white text-[15px] font-bold" style={{ fontFamily: '"Space Grotesk", sans-serif' }}>
                {formateur?.name}
              </h1>
              <p className="text-gray-500 text-[11px]">{currentMod?.title}</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <p className="text-gray-700 text-[10px] font-mono tracking-[5px] select-none">OPENROOM</p>
          </div>
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-6 py-5">
        {/* ─── STATS ─── */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
          {[
            { label: 'Leçons totales', value: stats.total, icon: <BookOpen size={14} />, color: '#94a3b8' },
            { label: 'Vidéos uploadées', value: stats.withVideo, icon: <Film size={14} />, color: '#22c55e' },
            { label: 'Slides', value: stats.withSlides, icon: <Presentation size={14} />, color: '#a855f7' },
            { label: 'Modifiées', value: stats.modified, icon: <Edit3 size={14} />, color: '#00c8ff' },
          ].map((s, i) => (
            <div key={i} className="rounded-xl px-4 py-3" style={{ backgroundColor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)' }}>
              <div className="flex items-center gap-2 mb-1.5">
                <span style={{ color: s.color }}>{s.icon}</span>
                <span className="text-gray-500 text-[11px]">{s.label}</span>
              </div>
              <p className="text-xl font-bold" style={{ color: s.color }}>{s.value}</p>
            </div>
          ))}
        </div>

        {/* Progress */}
        <div className="rounded-xl px-5 py-3 mb-5 flex items-center gap-4" style={{ backgroundColor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)' }}>
          <div className="flex-1">
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-gray-400 text-[12px] font-medium">Progression vidéos</span>
              <span className="text-white text-[12px] font-bold">{progress}%</span>
            </div>
            <div className="h-1.5 rounded-full bg-white/5">
              <div className="h-1.5 rounded-full transition-all duration-500" style={{ width: `${progress}%`, background: progress === 100 ? '#22c55e' : 'linear-gradient(90deg,#00c8ff,#0066ff)' }} />
            </div>
          </div>
          <span className="text-gray-600 text-[11px] flex-shrink-0">{stats.withVideo}/{stats.total}</span>
        </div>

        {/* ─── MAIN LAYOUT ─── */}
        <div className="flex gap-5" style={{ minHeight: 'calc(100vh - 280px)' }}>
          {/* LEFT: SIDEBAR - Sections & Lessons */}
          <div className="w-[380px] flex-shrink-0 space-y-2 overflow-y-auto pr-1" data-testid="formateur-lesson-list" style={{ maxHeight: 'calc(100vh - 300px)' }}>
            {modules.map(mod => (
              <div key={mod.id} className="space-y-2">
                {mod.sections.map((section, si) => {
                  const sKey = `${mod.id}-${si}`;
                  const isExpanded = expandedSections[sKey] !== false;
                  const deleted = isSectionDeleted(mod.id, si);
                  const title = getSectionTitle(mod.id, si, section.title);
                  const baseLessons = section.lessons || [];
                  const sCustom = customLessons.filter(c => c.module_id === mod.id && c.section_index === si);
                  const orderedLessons = getOrderedLessons(mod.id, si, baseLessons, sCustom);

                  if (deleted) return (
                    <div key={sKey} className="flex items-center gap-2 px-4 py-2.5 rounded-xl opacity-40 border border-dashed border-white/8">
                      <Trash2 size={11} className="text-red-400" />
                      <span className="text-gray-600 text-[12px] line-through flex-1">{title}</span>
                      <button onClick={() => handleRestoreSection(mod.id, si)} className="text-[10px] text-cyan-400 hover:underline flex items-center gap-1"><Undo2 size={10} />Restaurer</button>
                    </div>
                  );

                  return (
                    <div key={sKey} className="rounded-xl overflow-hidden" style={{ backgroundColor: 'rgba(255,255,255,0.015)', border: '1px solid rgba(255,255,255,0.05)' }}>
                      {/* Section header */}
                      <div className="flex items-center gap-1 px-3 py-2.5" style={{ borderBottom: isExpanded ? '1px solid rgba(255,255,255,0.04)' : 'none' }}>
                        <button onClick={() => setExpandedSections(p => ({ ...p, [sKey]: !isExpanded }))} className="p-0.5" data-testid={`toggle-section-${mod.id}-${si}`}>
                          {isExpanded ? <ChevronDown size={14} className="text-gray-500" /> : <ChevronRight size={14} className="text-gray-500" />}
                        </button>
                        {editingSection === sKey ? (
                          <div className="flex-1 flex items-center gap-2">
                            <input value={sectionTitle} onChange={e => setSectionTitle(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') handleRenameSection(mod.id, si); if (e.key === 'Escape') setEditingSection(null); }} autoFocus
                              className="flex-1 bg-black/50 border border-cyan-500/30 rounded-lg px-2 py-1 text-white text-[12px] focus:outline-none" />
                            <button onClick={() => handleRenameSection(mod.id, si)} className="p-1 text-cyan-400"><CheckCircle2 size={14} /></button>
                            <button onClick={() => setEditingSection(null)} className="p-1 text-gray-500"><X size={14} /></button>
                          </div>
                        ) : (
                          <>
                            <span className="text-white text-[13px] font-semibold flex-1 truncate">{title}</span>
                            <span className="text-gray-500 text-[11px] mr-2">{orderedLessons.length}</span>
                            <button data-testid={`rename-section-${mod.id}-${si}`} onClick={() => { setEditingSection(sKey); setSectionTitle(title); }} className="p-1.5 rounded-md text-gray-500 hover:text-cyan-400 hover:bg-cyan-500/10 transition-all" title="Renommer"><Edit3 size={14} /></button>
                            <button data-testid={`delete-section-${mod.id}-${si}`} onClick={() => handleDeleteSection(mod.id, si)} className="p-1.5 rounded-md text-gray-500 hover:text-red-400 hover:bg-red-500/10 transition-all" title="Supprimer"><Trash2 size={14} /></button>
                          </>
                        )}
                      </div>

                      {/* Lessons with DnD */}
                      {isExpanded && (
                        <div className="p-1.5 space-y-0.5">
                          <DndContext sensors={sensors} collisionDetection={closestCenter}
                            onDragStart={(e) => setDragActiveId(e.active.id)}
                            onDragEnd={(e) => handleDragEnd(e, mod.id, si, orderedLessons)}
                          >
                            <SortableContext items={orderedLessons.map(l => l.id || l.lesson_id)} strategy={verticalListSortingStrategy}>
                              {orderedLessons.map((lesson, li) => {
                                const lid = lesson.id || lesson.lesson_id;
                                return (
                                  <SortableLessonItem
                                    key={lid}
                                    id={lid}
                                    lesson={lesson}
                                    index={li}
                                    isActive={activeLesson === lid}
                                    hasOverride={!!overrides[lid]}
                                    hasVideo={!!files[`${lid}_video`]}
                                    hasSlides={!!files[`${lid}_slides`]}
                                    isCustom={lesson._isCustom || lesson.is_custom}
                                    onClick={() => openEditor(lesson)}
                                    onDelete={() => handleDeleteLesson(lid)}
                                    onPreview={() => setPreviewLesson(getLessonData(lesson))}
                                  />
                                );
                              })}
                            </SortableContext>
                          </DndContext>

                          {showAddLesson === sKey ? (
                            <AddLessonForm onAdd={(t) => handleAddLesson(si, t)} onCancel={() => setShowAddLesson(null)} saving={saving} />
                          ) : (
                            <button onClick={() => setShowAddLesson(sKey)} className="w-full flex items-center justify-center gap-1.5 py-2 rounded-lg text-[11px] text-gray-600 hover:text-cyan-400 hover:bg-cyan-500/5 transition-all border border-dashed border-white/5 hover:border-cyan-500/15" data-testid={`add-lesson-btn-${sKey}`}>
                              <Plus size={11} /> Ajouter une leçon
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}

                {/* Custom sections */}
                {customSections.filter(cs => cs.module_id === mod.id && !cs.is_deleted).map(cs => {
                  const sKey = `${mod.id}-${cs.section_index}`;
                  const isExpanded = expandedSections[sKey] !== false;
                  const sLessons = customLessons.filter(c => c.module_id === mod.id && c.section_index === cs.section_index);
                  return (
                    <div key={sKey} className="rounded-xl overflow-hidden" style={{ backgroundColor: 'rgba(0,200,255,0.02)', border: '1px solid rgba(0,200,255,0.08)' }}>
                      <div className="flex items-center gap-1 px-3 py-2.5">
                        <button onClick={() => setExpandedSections(p => ({ ...p, [sKey]: !isExpanded }))} className="p-0.5">{isExpanded ? <ChevronDown size={14} className="text-cyan-500" /> : <ChevronRight size={14} className="text-cyan-500" />}</button>
                        <Sparkles size={11} className="text-cyan-400" />
                        <span className="text-cyan-300 text-[13px] font-semibold flex-1 truncate">{cs.title}</span>
                        <span className="text-gray-600 text-[10px] mr-1">{sLessons.length}</span>
                        <button onClick={() => handleDeleteSection(mod.id, cs.section_index)} className="p-1 text-gray-600 hover:text-red-400"><Trash2 size={11} /></button>
                      </div>
                      {isExpanded && (
                        <div className="p-1.5 space-y-0.5">
                          {sLessons.map((cl, li) => (
                            <div key={cl.lesson_id} data-testid={`formateur-lesson-${cl.lesson_id}`}
                              className={`group flex items-center gap-2 rounded-lg px-3 py-2.5 cursor-pointer transition-all ${activeLesson === cl.lesson_id ? 'bg-[#0d2847] ring-1 ring-cyan-500/30' : 'hover:bg-white/[0.03]'}`}
                              onClick={() => openEditor({ ...cl, id: cl.lesson_id })}
                            >
                              <div className="w-6 h-6 rounded flex items-center justify-center bg-white/5 text-gray-600 text-[10px] font-bold">{li + 1}</div>
                              <p className="text-[13px] font-medium text-gray-200 truncate flex-1">{cl.title}</p>
                              <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                                <button onClick={(e) => { e.stopPropagation(); setPreviewLesson(cl); }} className="p-1.5 rounded-md text-gray-600 hover:text-cyan-400 hover:bg-cyan-500/10"><Eye size={13} /></button>
                                <button onClick={(e) => { e.stopPropagation(); handleDeleteLesson(cl.lesson_id); }} className="p-1.5 rounded-md text-gray-600 hover:text-red-400 hover:bg-red-500/10"><Trash2 size={13} /></button>
                              </div>
                            </div>
                          ))}
                          {showAddLesson === sKey ? (
                            <AddLessonForm onAdd={(t) => handleAddLesson(cs.section_index, t)} onCancel={() => setShowAddLesson(null)} saving={saving} />
                          ) : (
                            <button onClick={() => setShowAddLesson(sKey)} className="w-full flex items-center justify-center gap-1.5 py-2 rounded-lg text-[11px] text-gray-600 hover:text-cyan-400 hover:bg-cyan-500/5 transition-all border border-dashed border-cyan-500/10">
                              <Plus size={11} /> Ajouter une leçon
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}

                {/* Add section button */}
                {showAddSection === mod.id ? (
                  <div className="rounded-xl p-3 bg-violet-500/[0.03] border border-dashed border-violet-500/15" data-testid="add-section-form">
                    <input value={newSectionTitle} onChange={e => setNewSectionTitle(e.target.value)} placeholder="Nom de la nouvelle section..."
                      onKeyDown={e => { if (e.key === 'Enter') handleAddSection(mod.id); if (e.key === 'Escape') setShowAddSection(null); }}
                      className="w-full bg-transparent border-b border-white/10 pb-2 text-white text-[13px] placeholder:text-gray-600 focus:outline-none focus:border-violet-500/30 mb-2" autoFocus data-testid="new-section-title-input" />
                    <div className="flex gap-2">
                      <button onClick={() => handleAddSection(mod.id)} disabled={!newSectionTitle.trim()} className="px-4 py-1.5 rounded-lg text-[11px] text-white font-semibold disabled:opacity-40 bg-gradient-to-r from-violet-500 to-purple-600" data-testid="confirm-add-section-btn">Ajouter</button>
                      <button onClick={() => setShowAddSection(null)} className="px-3 py-1.5 text-[11px] text-gray-500 hover:text-gray-300">Annuler</button>
                    </div>
                  </div>
                ) : (
                  <button data-testid={`add-section-${mod.id}`} onClick={() => setShowAddSection(mod.id)} className="w-full flex items-center justify-center gap-1.5 py-2.5 rounded-xl text-[11px] text-gray-600 hover:text-violet-400 hover:bg-violet-500/5 transition-all border border-dashed border-white/5 hover:border-violet-500/15">
                    <Plus size={11} /> Nouvelle section
                  </button>
                )}
              </div>
            ))}
          </div>

          {/* RIGHT: EDITOR */}
          <div className="flex-1 min-w-0" data-testid="formateur-editor">
            {activeLesson ? (
              <div className="sticky top-[76px]">
                {/* Editor top bar */}
                <div className="flex items-center justify-between mb-4">
                  <button onClick={() => setActiveLesson(null)} className="text-gray-500 hover:text-white text-[12px] transition-all flex items-center gap-1" data-testid="editor-back-btn">
                    <ChevronLeft size={14} /> Retour
                  </button>
                  <div className="flex items-center gap-2">
                    {saved && <span className="text-emerald-400 text-[11px] flex items-center gap-1 animate-in fade-in" data-testid="save-success"><CheckCircle2 size={12} /> Sauvegardé</span>}
                    <button data-testid="formateur-preview-btn" onClick={() => setPreviewLesson(editData)}
                      className="flex items-center gap-2 px-4 py-2 rounded-xl text-[12px] font-medium text-gray-400 hover:text-white border border-white/10 hover:border-cyan-500/30 hover:bg-cyan-500/5 transition-all">
                      <Eye size={13} /> Aperçu étudiant
                    </button>
                    <button data-testid="formateur-save-btn" onClick={handleSave} disabled={saving}
                      className="flex items-center gap-2 px-5 py-2 rounded-xl text-white text-[12px] font-bold transition-all disabled:opacity-50 bg-gradient-to-r from-cyan-500 to-blue-600 hover:shadow-lg hover:shadow-cyan-500/20">
                      {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
                      {saving ? 'Sauvegarde...' : 'Sauvegarder'}
                    </button>
                  </div>
                </div>

                {/* Editor card */}
                <div className="rounded-2xl overflow-hidden" style={{ backgroundColor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)' }}>
                  {/* Title field */}
                  <div className="px-6 py-5 border-b border-white/[0.04]">
                    <label className="text-[10px] font-bold uppercase tracking-[2px] text-gray-600 block mb-2">Titre</label>
                    <input data-testid="formateur-edit-title" value={editData.title} onChange={e => setEditData(p => ({ ...p, title: e.target.value }))}
                      className="w-full bg-transparent text-white text-lg font-bold focus:outline-none placeholder:text-gray-700" style={{ fontFamily: '"Space Grotesk", sans-serif' }} placeholder="Titre de la leçon..." />
                  </div>

                  {/* Duration */}
                  <div className="px-6 py-4 border-b border-white/[0.04]">
                    <label className="text-[10px] font-bold uppercase tracking-[2px] text-gray-600 block mb-1">Durée</label>
                    <input data-testid="formateur-edit-duration" value={editData.duration} onChange={e => setEditData(p => ({ ...p, duration: e.target.value }))}
                      className="bg-transparent text-white text-sm focus:outline-none w-24 placeholder:text-gray-700" placeholder="10:00" />
                  </div>

                  {/* Description */}
                  <div className="px-6 py-4 border-b border-white/[0.04]">
                    <label className="text-[10px] font-bold uppercase tracking-[2px] text-gray-600 block mb-2">Description</label>
                    <textarea data-testid="formateur-edit-description" value={editData.description} onChange={e => setEditData(p => ({ ...p, description: e.target.value }))}
                      rows={3} className="w-full bg-transparent text-gray-300 text-[13px] leading-relaxed focus:outline-none resize-none placeholder:text-gray-700" placeholder="Décris le contenu de cette leçon..." />
                  </div>

                  {/* Script / Content */}
                  <div className="px-6 py-4 border-b border-white/[0.04]">
                    <label className="text-[10px] font-bold uppercase tracking-[2px] text-gray-600 block mb-2">Contenu / Script</label>
                    <textarea data-testid="formateur-edit-script" value={editData.script} onChange={e => setEditData(p => ({ ...p, script: e.target.value }))}
                      rows={12} className="w-full bg-transparent text-gray-300 text-[13px] leading-[1.8] focus:outline-none resize-none placeholder:text-gray-700"
                      placeholder={"Écris le contenu de ta leçon ici...\n\nPoints à aborder :\n- Point 1\n- Point 2\n\nConclusion :\nLe message clé à retenir"} />
                  </div>

                  {/* Upload zone */}
                  <div className="px-6 py-5">
                    <label className="text-[10px] font-bold uppercase tracking-[2px] text-gray-600 block mb-3">Fichiers</label>
                    <div className="grid grid-cols-2 gap-3">
                      {/* Video upload */}
                      <div onClick={() => handleUpload(activeLesson, 'video')} data-testid="upload-video-zone"
                        className={`rounded-xl p-4 text-center cursor-pointer transition-all group border border-dashed ${files[`${activeLesson}_video`] ? 'border-emerald-500/25 bg-emerald-500/[0.03]' : 'border-white/8 hover:border-emerald-500/20 hover:bg-emerald-500/[0.02]'}`}>
                        {uploading === `${activeLesson}_video` ? (
                          <Loader2 size={22} className="animate-spin text-emerald-400 mx-auto" />
                        ) : files[`${activeLesson}_video`] ? (
                          <>
                            <CheckCircle2 size={22} className="text-emerald-400 mx-auto mb-1.5" />
                            <p className="text-emerald-400 text-[12px] font-medium">Vidéo uploadée</p>
                            <p className="text-gray-600 text-[10px] mt-0.5 truncate">{files[`${activeLesson}_video`].original_filename}</p>
                            <p className="text-gray-700 text-[10px] mt-1 group-hover:text-emerald-400 transition-all">Remplacer</p>
                          </>
                        ) : (
                          <>
                            <Video size={22} className="text-gray-600 mx-auto mb-1.5 group-hover:text-emerald-400 transition-all" />
                            <p className="text-gray-500 text-[12px] group-hover:text-white transition-all">Uploader vidéo</p>
                            <p className="text-gray-700 text-[10px] mt-0.5">MP4, MOV, WebM</p>
                          </>
                        )}
                      </div>
                      {/* Slides upload */}
                      <div onClick={() => handleUpload(activeLesson, 'slides')} data-testid="upload-slides-zone"
                        className={`rounded-xl p-4 text-center cursor-pointer transition-all group border border-dashed ${files[`${activeLesson}_slides`] ? 'border-violet-500/25 bg-violet-500/[0.03]' : 'border-white/8 hover:border-violet-500/20 hover:bg-violet-500/[0.02]'}`}>
                        {uploading === `${activeLesson}_slides` ? (
                          <Loader2 size={22} className="animate-spin text-violet-400 mx-auto" />
                        ) : files[`${activeLesson}_slides`] ? (
                          <>
                            <CheckCircle2 size={22} className="text-violet-400 mx-auto mb-1.5" />
                            <p className="text-violet-400 text-[12px] font-medium">Slides uploadés</p>
                            <p className="text-gray-600 text-[10px] mt-0.5 truncate">{files[`${activeLesson}_slides`].original_filename}</p>
                            <p className="text-gray-700 text-[10px] mt-1 group-hover:text-violet-400 transition-all">Remplacer</p>
                          </>
                        ) : (
                          <>
                            <Presentation size={22} className="text-gray-600 mx-auto mb-1.5 group-hover:text-violet-400 transition-all" />
                            <p className="text-gray-500 text-[12px] group-hover:text-white transition-all">Uploader slides</p>
                            <p className="text-gray-700 text-[10px] mt-0.5">PDF, PPTX</p>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              /* Empty state */
              <div className="flex items-center justify-center" style={{ minHeight: 'calc(100vh - 300px)' }}>
                <div className="text-center max-w-sm">
                  <div className="w-16 h-16 rounded-2xl mx-auto mb-5 flex items-center justify-center bg-cyan-500/[0.06] border border-cyan-500/10">
                    <Edit3 size={24} className="text-cyan-500/40" />
                  </div>
                  <p className="text-white text-[15px] font-semibold mb-2" style={{ fontFamily: '"Space Grotesk", sans-serif' }}>Sélectionne une leçon</p>
                  <p className="text-gray-600 text-[12px] leading-relaxed">Clique sur une leçon dans la liste à gauche pour modifier son contenu, uploader ta vidéo et tes slides.</p>
                  <div className="mt-6 flex items-center justify-center gap-2 text-gray-700 text-[11px]">
                    <GripVertical size={12} />
                    <span>Glisse-dépose les leçons pour les réordonner</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
